import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import Backend from "i18next-http-backend";
import { initReactI18next } from "react-i18next";

import {
  FALLBACK_LANGUAGE,
  MeasurementSystems,
  SUPPORTED_LANGUAGES,
} from "@/features/app/constants";

i18n
  .use(Backend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init(
    {
      backend: {
        loadPath: "/locales/{{lng}}/{{ns}}.json",
      },
      compatibilityJSON: "v4",
      detection: {
        caches: [],
        order: ["querystring", "navigator"],
      },
      fallbackLng: FALLBACK_LANGUAGE,
      load: "languageOnly",
      nonExplicitSupportedLngs: true,
      react: {
        useSuspense: true,
      },
      supportedLngs: SUPPORTED_LANGUAGES,
    },
    (_err, _t) => {
      //
    },
  );
i18n.services.formatter.addCached("lowercase", (_lng, _options) => {
  return (value) => value.toLowerCase();
});

const formatUnit = (
  value: number,
  unit: "day" | "hour" | "minute" | "second",
  locale = "en",
) => {
  const formatter = new Intl.NumberFormat(locale, {
    style: "unit",
    unit,
    unitDisplay: unit === "day" || unit === "hour" ? "narrow" : "short",
  });
  return formatter.format(value);
};

i18n.services.formatter.add("relativetime", (value, lng, options) => {
  let totalSeconds = value;
  if (options?.unit !== "second") {
    totalSeconds = value * 60;
  }

  const days = Math.floor(totalSeconds / 86400);
  totalSeconds %= 86400;
  const hours = Math.floor(totalSeconds / 3600);
  totalSeconds %= 3600;
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;

  const parts = [];
  if (days > 0) parts.push(formatUnit(days, "day", lng));
  if (hours > 0) parts.push(formatUnit(hours, "hour", lng));
  if (mins > 0) parts.push(formatUnit(mins, "minute", lng));
  if (secs > 0 || (parts.length === 0 && options?.unit === "second")) {
    parts.push(formatUnit(secs, "second", lng));
  } else if (parts.length === 0) {
    parts.push(formatUnit(0, "minute", lng));
  }

  return parts.join(" ");
});

i18n.services.formatter.add("relativedistance", (value, lng, options) => {
  const measurementSystem =
    options?.measurement_system ?? MeasurementSystems.metric;
  if (!measurementSystem || measurementSystem === MeasurementSystems.metric) {
    return new Intl.NumberFormat(lng, {
      maximumFractionDigits: 2,
      // unitDisplay: "narrow",
      minimumFractionDigits: 0,
      style: "unit",
      unit: value > 1000 ? "kilometer" : "meter",
    }).format(value > 1000 ? value / 1000 : value);
  } else {
    const feet = Math.floor(value * 3.28084);

    let unit: "foot" | "mile";
    let amount: number;
    // 0.25 miles
    if (feet < 1320) {
      unit = "foot";
      amount = feet;
    } else {
      unit = "mile";
      amount = value / 1609.34;
    }

    return new Intl.NumberFormat(lng, {
      maximumFractionDigits: unit === "mile" ? 2 : 0,
      minimumFractionDigits: 0,
      style: "unit",
      unit,
    }).format(amount);
  }
});

export default i18n;
